import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Users, 
  Target, 
  Award,
  CheckCircle,
  BarChart3,
  Globe,
  Lock
} from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Independent Analysis',
    description: 'Our rankings are audited by internal teams with deep due diligence on every fund before ranking.'
  },
  {
    icon: Users,
    title: 'Expert Committee',
    description: 'Our independent investment committee comprises global investors with decades of experience.'
  },
  {
    icon: Target,
    title: 'Data-Driven Insights',
    description: 'Advanced analytics offering a clear picture of fund performance, risk factors, and growth potential.'
  },
  {
    icon: BarChart3,
    title: 'Comprehensive Coverage',
    description: 'Track funds across multiple regions including US, UK, Australia, Switzerland, and global markets.'
  }
];

const values = [
  { icon: CheckCircle, text: 'Transparency in methodology' },
  { icon: CheckCircle, text: 'Accuracy in data reporting' },
  { icon: CheckCircle, text: 'Accessibility for all investors' },
  { icon: CheckCircle, text: 'Financial literacy advocacy' }
];

export const AboutSection = () => {
  return (
    <section id="about" className="py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge 
            variant="outline" 
            className="mb-4 border-accent/40 text-accent font-sans text-xs uppercase tracking-wider"
          >
            About Us
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            About Top Fund Rankings
          </h2>
          <p className="text-muted-foreground font-sans leading-relaxed text-lg">
            TopFundRankings.com is an innovative investment platform that offers annual investment 
            rankings and comprehensive information to guide individuals in making informed investment 
            decisions in the US and Global market.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card key={index} className="card-premium border-border bg-card">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold text-foreground">
                Our Mission
              </h3>
              <p className="text-muted-foreground font-sans leading-relaxed">
                Top Fund Rankings is dedicated to empowering investors by providing a reliable, 
                comprehensive, and accessible resource for navigating the complex world of investment funds. 
                Our platform is built on a foundation of transparency, accuracy, and data-driven insights.
              </p>
              <p className="text-muted-foreground font-sans leading-relaxed">
                Whether you are a seasoned investment professional seeking the latest market trends, 
                a financial advisor guiding your clients, or a beginner taking your first steps into 
                investing, Top Fund Rankings is here to support you.
              </p>
            </div>

            {/* Values List */}
            <div className="grid grid-cols-2 gap-3">
              {values.map((value, index) => (
                <div key={index} className="flex items-center gap-2">
                  <value.icon className="h-4 w-4 text-accent" />
                  <span className="text-sm text-foreground font-sans">{value.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Card */}
          <Card className="border-border bg-primary text-primary-foreground overflow-hidden">
            <CardContent className="p-8 relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
                }} />
              </div>
              
              <div className="relative z-10 space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Award className="h-8 w-8 text-accent" />
                  <h3 className="font-serif text-xl font-bold">
                    Why Choose Us
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-primary-foreground/5">
                    <Globe className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-primary-foreground mb-1">Global Coverage</h4>
                      <p className="text-sm text-primary-foreground/70">
                        Rankings across 25+ countries and all major markets
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-lg bg-primary-foreground/5">
                    <Lock className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-primary-foreground mb-1">Rigorous Due Diligence</h4>
                      <p className="text-sm text-primary-foreground/70">
                        Every fund is thoroughly vetted before inclusion
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-lg bg-primary-foreground/5">
                    <BarChart3 className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-primary-foreground mb-1">Advanced Analytics</h4>
                      <p className="text-sm text-primary-foreground/70">
                        Performance, risk factors, and growth potential analysis
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
